import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Star, Trash2, CheckCircle2 } from "lucide-react";

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

const Stars = ({ n }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={13} className={i <= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"} />
    ))}
  </span>
);

export default function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [gsync, setGsync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [nudgeStats, setNudgeStats] = useState([]);

  const loadGsync = () => api.get("/admin/reviews/google-status").then((r) => setGsync(r.data)).catch(() => {});
  const loadNudge = () => api.get("/admin/review-nudge-stats").then((r) => setNudgeStats(r.data)).catch(() => {});

  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); loadGsync(); loadNudge(); }, []);

  const approve = async (id) => {
    try {
      await api.patch(`/admin/reviews/${id}/approve`);
      toast.success("Review approved — now live on the homepage");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review from ${r.name}?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/reviews/sync-google");
      if (data.skipped || data.error) toast.error(data.skipped || data.error);
      else toast.success(`Synced ${data.synced} new Google review(s) — 4 stars and up only`);
      load();
      loadGsync();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="admin-reviews-tab">
      <AdminHeader
        testid="admin-header-reviews"
        eyebrow="Marketing"
        title="Reviews"
        description="Approve customer reviews to show them on the homepage."
        actions={<Button data-testid="sync-google-reviews" onClick={syncNow} disabled={syncing} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui shrink-0">{syncing ? "Syncing…" : "Sync Google Reviews"}</Button>}
      />
      {gsync && (
        <p data-testid="google-sync-status" className="-mt-4 mb-6 text-xs font-ui text-[#4A4A4A]">
          Google sync: {gsync.key_configured ? `active · last run ${gsync.last_sync ? new Date(gsync.last_sync).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never"} · ${gsync.last_sync_count ?? 0} new last run${gsync.google_rating ? ` · Google rating ${gsync.google_rating} (${gsync.google_rating_count} reviews)` : ""}` : "waiting for GOOGLE_PLACES_API_KEY in backend env"}
        </p>
      )}
      {nudgeStats.length > 0 && (
        <div data-testid="review-nudge-ab-stats" className="mb-6 grid gap-4 sm:grid-cols-2">
          {nudgeStats.map((s) => (
            <div key={s.variant} data-testid={`nudge-variant-${s.variant}`} className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5">
              <p className="text-[10px] font-ui tracking-[0.2em] uppercase text-muted-foreground">Review nudge · {s.label}</p>
              <p className="mt-2 text-3xl font-display text-[#1A1A1A]">{s.click_rate_pct}%</p>
              <p className="mt-1 text-xs font-ui text-muted-foreground">{s.clicked} of {s.nudged} guests tapped the review link</p>
            </div>
          ))}
        </div>
      )}
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Customer</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Review</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.length === 0 && (
              <TableRow className={ROW}>
                <TableCell colSpan={7} data-testid="reviews-empty" className="text-muted-foreground font-ui py-10 text-center">No reviews yet — they're emailed to customers after completed rentals.</TableCell>
              </TableRow>
            )}
            {reviews.map((r) => (
              <TableRow key={r.id} data-testid={`review-row-${r.id}`} className={ROW}>
                <TableCell className="font-ui font-semibold text-[#1A1A1A]">{r.name}</TableCell>
                <TableCell className="text-muted-foreground font-ui text-xs">{r.vehicle_name}</TableCell>
                <TableCell><Stars n={r.rating} /></TableCell>
                <TableCell className="text-muted-foreground max-w-[260px]"><span className="line-clamp-2 text-sm">{r.text}</span></TableCell>
                <TableCell className="text-muted-foreground font-ui text-xs whitespace-nowrap">{fmtDate(r.created_at?.slice(0, 10))}</TableCell>
                <TableCell>
                  <span className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${r.approved ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-amber-700 border-amber-200 bg-amber-50"}`}>
                    {r.approved ? "Live" : "Pending"}
                  </span>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {!r.approved && (
                    <Button data-testid={`review-approve-${r.id}`} size="sm" variant="outline" onClick={() => approve(r.id)} className="rounded-full border-emerald-200 bg-transparent text-emerald-700 hover:bg-emerald-50 font-ui mr-2">
                      <CheckCircle2 size={13} className="mr-1.5" /> Approve
                    </Button>
                  )}
                  <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r)} className="text-muted-foreground hover:text-red-700 transition-colors duration-200" aria-label="Delete review">
                    <Trash2 size={15} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
